import api from './api';

const transactionService = {
  /**
   * Get transactions with filtering and pagination
   */
  async getTransactions(params = {}) {
    const response = await api.get('/transactions', { params });
    return response.data;
  },

  /**
   * Get transaction statistics
   */
  async getStats() {
    const response = await api.get('/transactions/stats');
    return response.data;
  },

  /**
   * Get a single transaction
   * @param {string} id - Transaction ID
   * @param {string} reportingCurrency - Currency to convert amounts to (default: USD)
   */
  async getTransaction(id, reportingCurrency = 'USD') {
    const params = { reportingCurrency };
    const response = await api.get(`/transactions/${id}`, { params });
    return response.data;
  },

  /**
   * Import CMC CSV file
   */
  async importCMC(file, accountId = null) {
    const formData = new FormData();
    formData.append('file', file);
    if (accountId) {
      formData.append('account_id', accountId);
    }

    const response = await api.post('/transactions/import-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * Import CSV file with custom mappings
   * @param {File} file - CSV file
   * @param {string} accountId - Account ID
   * @param {Object} columnMappings - Column to field mappings
   * @param {Object} categoryMappings - Type to category mappings
   * Day 26: parses CSV via parser registry. Returns a ParseResult,
   * does NOT auto-import. Caller routes the result to AIPreviewStep,
   * then calls aiImport() on user confirm to commit.
   *
   * Renamed from importWithMappings (the old name was misleading after
   * the parse-and-preview semantics change).
   *
   * @param {File} file - CSV file
   * @param {string} accountId - REQUIRED — backend rejects without
   * @returns {Promise<{success, transactions, skippedRows, warnings,
   *   columnMapping, detectedBroker, parserVersion, rowsProcessed,
   *   rowsByCategory}>}
   *   On no-parser-match: { success: false, errorCode: 'unknown_format',
   *   message, fingerprint, detected_headers }
   */
  async parseCSVWithRegistry(file, accountId) {
    const formData = new FormData();
    formData.append('file', file);
    if (accountId) {
      formData.append('account_id', accountId);
    }
    const response = await api.post('/transactions/import-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * Create a manual transaction
   */
  async createManual(data) {
    const response = await api.post('/transactions/manual', data);
    return response.data;
  },

  /**
   * The open trades a typed sale can close, in an account no file feeds
   */
  async getManualOpenTrades(accountId) {
    const response = await api.get('/transactions/manual/open-trades', { params: { account_id: accountId } });
    return response.data;
  },

  /**
   * Update a transaction
   */
  async updateTransaction(id, data) {
    const response = await api.put(`/transactions/${id}`, data);
    return response.data;
  },

  /**
   * Delete a transaction
   */
  async deleteTransaction(id) {
    const response = await api.delete(`/transactions/${id}`);
    return response.data;
  },

  /**
   * Approve a transaction
   */
  async approveTransaction(id) {
    const response = await api.put(`/transactions/${id}/approve`);
    return response.data;
  },

  /**
   * Bulk approve transactions
   */
  async bulkApprove(transactionIds) {
    const response = await api.post('/transactions/bulk-approve', { transactionIds });
    return response.data;
  },

  /**
   * Un-reconcile a transaction (Remove & Redo)
   */
  async unreconcileTransaction(id) {
    const response = await api.put(`/transactions/${id}/unreconcile`);
    return response.data;
  },

  /**
   * Bulk un-reconcile transactions
   */
  async bulkUnreconcile(transactionIds) {
    const response = await api.post('/transactions/bulk-unreconcile', { transactionIds });
    return response.data;
  },

  /**
   * Bulk allocate transactions to a trading system
   */
  async bulkAllocate(transactionIds, allocatedToSystem) {
    const response = await api.post('/transactions/bulk-allocate', {
      transactionIds,
      allocated_to_system: allocatedToSystem
    });
    return response.data;
  },

  /**
   * Get portfolio reconciliation transactions
   * @param {string} reportingCurrency - Currency to convert amounts to
   */
  async getPortfolioTransactions(reportingCurrency) {
    const params = reportingCurrency ? { reportingCurrency } : {};
    const response = await api.get('/transactions/portfolio', { params });
    return response.data;
  },

  /**
   * How many cash rows (deposits, dividends, fees…) are waiting on the
   * Portfolio tab — the dashboard counter used to count trade opens only, so
   * five waiting deposits read as "0" (3 Oct 2026). One row, just the total.
   */
  async getPortfolioQueueCount() {
    const response = await api.get('/transactions/portfolio', { params: { limit: 1, status: 'waiting' } });
    return response.data?.data?.total ?? response.data?.total ?? 0;
  },

  /**
   * Get Review/Unmatched transactions for the Review tab
   */
  async getReviewTransactions() {
    const response = await api.get('/transactions/review');
    return response.data;
  },

  /**
   * Get trade reconciliation transactions
   */
  async getTradeTransactions(scope) {
    // Item 85 — scope 'options' returns ONLY option transactions (the
    // dedicated Options recon tab); default returns everything but options.
    const response = await api.get('/transactions/trades', {
      params: scope ? { scope } : {}
    });
    return response.data;
  },

  /**
   * Get import history
   */
  async getImportHistory(params = {}) {
    const response = await api.get('/transactions/import-history', { params });
    return response.data;
  },

  /**
   * Get import deletion impact analysis
   */
  async getImportImpact(batchId) {
    const response = await api.get(`/transactions/imports/${batchId}/impact`);
    return response.data;
  },

  /**
   * Delete an import batch and all its transactions.
   *
   * @param {string} batchId
   * @param {{ unreconcileFirst?: boolean }} [options]
   *   unreconcileFirst — Task 2.4 one-click "Unreconcile and delete".
   *   When true, the backend atomically unreconciles every linked
   *   trade in the batch BEFORE deleting (under a single transaction,
   *   rollback on any failure). When false/omitted, a batch with
   *   reconciled txns returns 409 with the affected_trades payload.
   */
  async deleteImportBatch(batchId, options = {}) {
    const params = {};
    if (options.unreconcileFirst === true) {
      params.unreconcile_first = 'true';
    }
    const response = await api.delete(
      `/transactions/imports/${batchId}`,
      { params }
    );
    return response.data;
  },

  /**
   * Bulk update transaction categories (post-import review)
   * @param {Array} updates - [{id, category}]
   * @param {string} batchId - Import batch ID for tracking
   */
  async bulkUpdateCategories(updates, batchId = null) {
    const response = await api.post('/transactions/bulk-update-categories', {
      updates,
      batchId
    });
    return response.data;
  },

  /**
   * Combine multiple partial fill transactions into one
   * Used for combining partial fills from broker executions
   * @param {Array<string>} transactionIds - IDs of transactions to combine
   */
  async combineTransactions(transactionIds) {
    const response = await api.post('/transactions/combine', { transactionIds });
    return response.data;
  },

  /**
   * Parse CSV data using AI (Claude Haiku)
   * @param {string[]} headers - CSV column headers
   * @param {string[][]} rows - All rows (backend pre-scans for samples, maps all server-side)
   * @param {string} accountCurrency - Account base currency
   * @param {string} [brokerName] - Optional broker hint for AI context
   * @returns {Object} AI parsing result with transactions, mappings, warnings
   */
  async aiParseCSV(headers, rows, accountCurrency = 'AUD', brokerName = null) {
    const response = await api.post('/transactions/ai-parse', {
      headers,
      rows,
      accountCurrency,
      brokerName
    });
    return response.data;
  },

  /**
   * AI structure analysis for MappingForm pre-fill. Phase 2 Day 13.
   *
   * POSTs headers + sample rows to /ai-analyze-structure, which routes
   * through Haiku-tier Anthropic call with broker fingerprint priors,
   * currency-from-header detection, split debit/credit + TextDate
   * fallback transformation emission, and confidence rating.
   *
   * Always resolves with a parsed body (backend returns 200 even on AI
   * failure, with success:false in the response). Caller checks the
   * success field and falls back to empty MappingForm initialMapping
   * when the AI is unavailable.
   *
   * @param {Object} params
   * @param {string[]} params.headers
   * @param {string[][]} params.rows
   * @param {string} [params.accountCurrency='AUD']
   * @param {string|null} [params.brokerName=null] — optional user hint
   * @returns {Object} {
   *   success, columnMapping, typeMapping, confidence,
   *   detectedBroker, inferredCurrency, dateFormat,
   *   transformations: [...], warnings: [...]
   * }
   */
  /**
   * Which account does this file belong to? Fingerprint → broker → the
   * user's accounts at that broker. Never guesses across brokers; an
   * unrecognised file returns detected:null. Writes nothing.
   */
  async detectAccount(headers) {
    const response = await api.post('/transactions/detect-account', { headers });
    return response.data?.data || { detected: null, candidates: [], suggestedAccountId: null };
  },

  async analyzeStructure({ headers, rows, accountCurrency = 'AUD', brokerName = null }) {
    const response = await api.post('/transactions/ai-analyze-structure', {
      headers,
      rows,
      accountCurrency,
      brokerName
    });
    return response.data;
  },

  /**
   * Convert an uploaded XLSX file to CSV-shape JSON server-side.
   * Phase 2.4. The backend uses ExcelJS to read the file in memory and
   * returns ALL sheets pre-converted (single round-trip). Frontend caches
   * the response and switches between sheets without re-uploading.
   *
   * @param {File} file - browser File object (.xlsx or .xlsm, ≤25MB)
   * @returns {Object} { success, sheets: [{name, headers, rows, totalRows}, ...],
   *                     defaultSheetIndex, fileType: 'xlsx' }
   */
  async xlsxParse(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/transactions/xlsx-parse', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * Apply a user-supplied column + type mapping to CSV rows server-side
   * (no AI call). Phase 2.3.3a / Path A.
   *
   * Used when the user has chosen the mapping themselves via MappingForm —
   * the backend just runs `applyMappingToAllRows` deterministically. Same
   * response shape as aiParseCSV so the rest of AIImportWizard /
   * AIPreviewStep consumes it identically.
   *
   * @param {Object} params
   * @param {string[]} params.headers
   * @param {string[][]} params.rows
   * @param {Object} params.columnMapping        — must have date/amount/type set
   * @param {Object} [params.typeMapping]        — defaults {} server-side
   * @param {string} [params.dateFormat]         — defaults 'DD/MM/YYYY'
   * @param {string} [params.accountCurrency]    — defaults 'AUD'
   * @param {Object} [params.directionMap]
   * @param {string} [params.brokerHint]         — logging only; no inference
   * @returns {Object} { success, transactions, skippedRows, warnings, summary,
   *                     columnMapping, typeMapping, dateFormat,
   *                     detectedBroker: null, source: 'manual_mapping' }
   */
  async applyMapping({
    headers,
    rows,
    columnMapping,
    typeMapping = {},
    dateFormat = 'DD/MM/YYYY',
    accountCurrency = 'AUD',
    directionMap = {},
    brokerHint = null
  }) {
    const response = await api.post('/transactions/apply-mapping', {
      headers,
      rows,
      columnMapping,
      typeMapping,
      dateFormat,
      accountCurrency,
      directionMap,
      brokerHint
    });
    return response.data;
  },

  /**
   * Get batch info for AI parsing
   * @param {number} totalRows - Total number of rows in the CSV
   * @returns {Object} { batchSize, batchCount, totalRows }
   */
  async aiParseBatchInfo(totalRows) {
    const response = await api.post('/transactions/ai-parse-batch-info', { totalRows });
    return response.data;
  },

  /**
   * Parse a single batch of CSV data using AI
   * @param {Object} params - Batch parsing parameters
   * @returns {Object} AI parsing result for this batch
   */
  async aiParseBatch({
    headers,
    rows,
    accountCurrency,
    batchNumber,
    totalBatches,
    columnMapping,
    detectedBroker,
    rowOffset
  }) {
    const response = await api.post('/transactions/ai-parse-batch', {
      headers,
      rows,
      accountCurrency,
      batchNumber,
      totalBatches,
      columnMapping,
      detectedBroker,
      rowOffset
    });
    return response.data;
  },

  /**
   * Import AI-parsed transactions
   * @param {Array} transactions - AI-parsed transaction objects
   * @param {string} accountId - Target account ID
   * @param {Object} columnMapping - The column mapping used
   * @param {string} detectedBroker - Detected broker name
   */
  async aiImport(transactions, accountId, columnMapping = null, detectedBroker = null) {
    const response = await api.post('/transactions/ai-import', {
      transactions,
      accountId,
      columnMapping,
      detectedBroker
    });
    return response.data;
  },

  /**
   * Check for duplicate transactions before importing
   * @param {string[]} sourceReferences - Array of source_reference values to check
   * @returns {Object} { duplicates: string[], newCount: number, duplicateCount: number }
   */
  async checkDuplicates(sourceReferences) {
    const response = await api.post('/transactions/check-duplicates', { sourceReferences });
    return response.data;
  },

  /**
   * Record a fingerprint correction from the AI preview
   */
  async recordFingerprintCorrection(headerHash, brokerType, aiCategory, userCategory) {
    const response = await api.post('/transactions/fingerprint-correction', {
      headerHash, brokerType, aiCategory, userCategory
    });
    return response.data;
  }
};

export default transactionService;
